import _ from "lodash";
import {
  query,
  orderBy,
  startAfter,
  startAt,
  limit,
  collection,
  doc,
  and,
  or,
  where,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  type Unsubscribe,
  serverTimestamp,
  increment,
  DocumentReference,
  Query,
  documentId,
  Timestamp,
  FirestoreError,
  CollectionReference,
  type QueryFilterConstraint,
  type QueryNonFilterConstraint,
  type QuerySnapshot,
  type DocumentData,
  type DocumentChange,
} from "firebase/firestore";
import type {
  ITalkRoomRepository,
  talkRoomObserverType,
  talkUserDataObserverType,
  talkObserverType,
  userObserverType,
  userGoodObserverType,
  threadTalkObserverType,
  ObservedTalkRoom,
} from "~/interfaces/ITalkRoomRepository";
import { TalkRoom } from "~/entities/TalkRoom";
import { Talk, TalkConverter } from "~/entities/Talk";
import { TalkRoomUser, TalkRoomUserConverter } from "~/entities/TalkRoomUser";
import { TalkLastRead, TalkLastReadConverter } from "~/entities/TalkLastRead";
import type { TalkGoodUserResponse } from "~/entities/TalkGoodUserResponse";
import {
  TALK_BOOKMARK_PER_ROOM_MAX,
  TALK_ROOM_TOTAL_MAX,
  TALK_SEARCH_PERIOD_DAYS,
  TALK_UPDATE_DEBOUNCE_TIME,
} from "~/consts/TalkConfig";
import { TalkUserData, TalkUserDataKeys, TalkUserDataKeysConverter } from "~/entities/TalkUserData";
import { FIRESTORE_WHERE_IN_LIMIT } from "~/consts/Firebase";

/* データ構成

トーク
  /talks/{plan_id}/talkRooms/{talk_room_id}/talks/{talk_id:Talk}

ユーザーデータ
  /talks/{plan_id}/users/{user_id:TalkRoomUser}

いいね管理データ
  /talks/{plan_id}/talkRooms/{talk_room_id}/talks/{talk_id}/favoriteUsers/{user_id}

既読管理データ
  /users/{user_id}/talkRooms/{talk_room_id:lastReadTalkId}

ユーザー別いいねデータ
  /users/{user_id}/talkRooms/{talk_room_id}/likes/{talk_id}
*/

const baseCollectionName = "talks";
const planCollectionName = "plans";
const talkRoomCollectionName = "talkRooms";
const talkCollectionName = "talks";
const talkThreadCollectionName = "talkThreads";
const talkBookmarkCollectionName = "talkBookmarks";
const talkMentionCollectionName = "talkMentions";
const userCollectionName = "users";
const favoriteCollectionName = "favoriteUsers";
const userGoodCollectionName = "likes";

// デバッグ用監視ON/OFF制御フラグ
// https://fincs.backlog.com/view/FINCS-3302
// https://arkad-co.slack.com/archives/C06P8P9L95G/p1738121284853849?thread_ts=1738045710.950449&cid=C06P8P9L95G
const ENABLE_ON_SNAPSHOT = true;

export class TalkRoomRepository implements ITalkRoomRepository {
  // トークルーム一覧の監視用
  talkRoomsUnsubscribe: Unsubscribe | undefined = undefined;

  // トークスレッドの監視用
  talkThreadsUnsubscribe: Unsubscribe | undefined = undefined;
  talkThreadsUnreadUnsubscribe: Unsubscribe | undefined = undefined;

  // トークブックマークの監視用
  talkBookmarksUnsubscribe: Unsubscribe | undefined = undefined;

  // トークメンションの監視用
  talkMentionsUnsubscribe: Unsubscribe | undefined = undefined;
  talkMentionsUnreadUnsubscribe: Unsubscribe | undefined = undefined;

  // トークルームのトークの監視用
  talksUnsubscribe: Unsubscribe | undefined = undefined;

  // トークルームのトークスレッドの監視用
  threadTalksUnsubscribe: Unsubscribe | undefined = undefined;

  // トークルームのユーザー監視用
  usersAddUnsubscribe: Unsubscribe | undefined = undefined;
  usersChangeUnsubscribe: Unsubscribe | undefined = undefined;

  // トークルームのいいね監視用
  userGoodsUnsubscribe: Unsubscribe | undefined = undefined;

  // 変更コールバックのデバウンス管理用
  // トークID単位でデバウンスさせる（でないとデバウンス待ちの間に別トークの更新が入ると反映漏れしてしまうため）
  debounceChangeCallbacks = new Map<string, _.DebouncedFunc<(talk: Talk) => void>>();

  getTalkRoomCollection(planId: string) {
    const { $firestore } = useNuxtApp();
    return collection($firestore, baseCollectionName, planId, talkRoomCollectionName);
  }

  getUserTalkRoomCollection(userId: string) {
    const { $firestore } = useNuxtApp();
    return collection($firestore, userCollectionName, userId, talkRoomCollectionName).withConverter(
      TalkLastReadConverter,
    );
  }

  startTalkRoomsObserve(planId: string, addCallBack: talkRoomObserverType, changeCallBack: talkRoomObserverType): void {
    if (!ENABLE_ON_SNAPSHOT) {
      return;
    }
    this.stopTalkRoomsObserve();

    const convDocChange = (change: DocumentChange) => {
      const data = change.doc.data();
      return {
        id: change.doc.id,
        latesteTalkID: data.LatesteTalkID,
        latestUserId: data.LatestUserId,
        latestPostDateTime: data.LatestPostDateTime?.toDate(),
        latestIsDelivery: data.LatestIsDelivery,
      } as ObservedTalkRoom;
    };

    const collection = this.getTalkRoomCollection(planId);
    const q = query(collection);
    this.talkRoomsUnsubscribe = onSnapshot(q, snapshot => {
      snapshot.docChanges().forEach(
        change => {
          const observed = convDocChange(change);
          if (change.type === "modified") {
            changeCallBack(observed);
          } else if (change.type === "added") {
            addCallBack(observed);
          }
        },
        (error: FirestoreError) => {
          consoleWarn(`startTalkRoomsObserve onSnapshot: ${error.message}`, error);
        },
      );
    });
  }

  stopTalkRoomsObserve() {
    if (this.talkRoomsUnsubscribe) {
      this.talkRoomsUnsubscribe();
      this.talkRoomsUnsubscribe = undefined;
    }
  }

  private listenUserData(
    collection: CollectionReference<TalkUserDataKeys>,
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
    oldestTalkId: number,
    showThreads = false,
  ): Unsubscribe {
    if (!ENABLE_ON_SNAPSHOT) {
      return () => {};
    }
    // 取得済みかそれより新しい範囲のデータを監視する
    const q = query(collection, where("talkId", ">=", oldestTalkId));
    return onSnapshot(q, snapshot => {
      snapshot.docChanges().forEach(
        async change => {
          // change.type は、 added,modified,removed のいずれか (firestore 仕様)
          const ext = await this.convertToTalkUserData(planId, userId, change.doc.data(), showThreads);
          callback(ext, change.type);
        },
        (error: FirestoreError) => {
          consoleWarn(`listenUserData change onSnapshot: ${error.message}`, error);
        },
      );
    });
  }

  startTalkThreadsObserve(
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
    oldestTalkId: number,
  ): void {
    this.stopTalkThreadsObserve();
    this.talkThreadsUnsubscribe = this.listenUserData(
      this.getThreadsCollection(userId, planId),
      planId,
      userId,
      callback,
      oldestTalkId,
      true,
    );
  }

  stopTalkThreadsObserve(): void {
    if (this.talkThreadsUnsubscribe) {
      this.talkThreadsUnsubscribe();
      this.talkThreadsUnsubscribe = undefined;
    }
  }

  startTalkBookmarksObserve(
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
    oldestTalkId: number,
  ): void {
    this.stopTalkBookmarksObserve();
    this.talkBookmarksUnsubscribe = this.listenUserData(
      this.getBookmarksCollection(userId, planId),
      planId,
      userId,
      callback,
      oldestTalkId,
    );
  }

  stopTalkBookmarksObserve(): void {
    if (this.talkBookmarksUnsubscribe) {
      this.talkBookmarksUnsubscribe();
      this.talkBookmarksUnsubscribe = undefined;
    }
  }

  startTalkMentionsObserve(
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
    oldestTalkId: number,
  ): void {
    this.stopTalkMentionsObserve();
    this.talkMentionsUnsubscribe = this.listenUserData(
      this.getMentionsCollection(userId, planId),
      planId,
      userId,
      callback,
      oldestTalkId,
    );
  }

  stopTalkMentionsObserve(): void {
    if (this.talkMentionsUnsubscribe) {
      this.talkMentionsUnsubscribe();
      this.talkMentionsUnsubscribe = undefined;
    }
  }

  // トークユーザー講座データを取得（スレッド・メンション既読情報など）
  getUserPlanDocument(userId: string, planId: string) {
    const { $firestore } = useNuxtApp();
    return doc($firestore, userCollectionName, userId, planCollectionName, planId);
  }

  // トークユーザーデータ未読情報の監視（スレッド・メンション）
  onSnapshotUserDataUnread(
    collection: CollectionReference<TalkUserDataKeys>,
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
    currentLastReadAt: Timestamp,
    updateLastReadAtFunc: (lastReadAt: Date) => Promise<void>,
    showThreads: boolean,
  ) {
    // 既読情報更新関数。大量に呼ばれる場合があるが最新のみ反映すれば良いので、デバウンスでバースト抑止
    const debounceUpdateLastReadAt = _.debounce(async (lastReadAt: Date) => {
      await updateLastReadAtFunc(lastReadAt);
    }, 500);

    // 既読判定済みより新しい範囲のデータを監視する
    const q = query(collection, where("createdAt", ">", currentLastReadAt), orderBy("createdAt", "asc"));
    return onSnapshot(q, snapshot => {
      snapshot.docChanges().forEach(
        async change => {
          const ext = await this.convertToTalkUserData(planId, userId, change.doc.data(), showThreads);
          if (!ext.hasUnread && change.type !== "removed") {
            // 既読であれば、ユーザーデータの既読日時を更新
            // removed 以外 = added, modified となる。createdAt はクエリで asc ソートしており、常に最新が保障される
            debounceUpdateLastReadAt(ext.createdAt);
          }
          callback(ext, change.type);
        },
        (error: FirestoreError) => {
          consoleWarn(`onSnapshotUserDataUnread onSnapshot: ${error.message}`, error);
        },
      );
    });
  }

  // トークスレッド未読情報の監視を開始
  async startTalkThreadsUnreadObserve(
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
  ): Promise<void> {
    this.stopTalkThreadsUnreadObserve();

    // スレッド既読判定済み日時を取得
    const d = this.getUserPlanDocument(userId, planId);
    const snap = await getFirestoreDoc(d);
    const lastReadAtDefault = Timestamp.fromDate(new Date("2023-01-01T00:00:00Z"));
    const lastReadAt = snap.exists() ? (snap.data().threadLastReadAt ?? lastReadAtDefault) : lastReadAtDefault;

    // スレッド既読判定済み日時以降を監視
    this.talkThreadsUnreadUnsubscribe = this.onSnapshotUserDataUnread(
      this.getThreadsCollection(userId, planId),
      planId,
      userId,
      callback,
      lastReadAt,
      async (lastReadAt: Date) => {
        await setDoc(d, { threadLastReadAt: lastReadAt }, { merge: true });
      },
      true,
    );
  }

  // トークスレッド未読情報の監視を停止
  stopTalkThreadsUnreadObserve(): void {
    if (this.talkThreadsUnreadUnsubscribe) {
      this.talkThreadsUnreadUnsubscribe();
      this.talkThreadsUnreadUnsubscribe = undefined;
    }
  }

  // トークメンション未読情報の監視を開始
  async startTalkMentionsUnreadObserve(
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
  ): Promise<void> {
    this.stopTalkMentionsUnreadObserve();

    // メンション既読判定済み日時を取得
    const d = this.getUserPlanDocument(userId, planId);
    const snap = await getFirestoreDoc(d);
    const lastReadAtDefault = Timestamp.fromDate(new Date("2023-01-01T00:00:00Z"));
    const lastReadAt = snap.exists() ? (snap.data().mentionLastReadAt ?? lastReadAtDefault) : lastReadAtDefault;

    // メンション既読判定済み日時以降を監視
    this.talkMentionsUnreadUnsubscribe = this.onSnapshotUserDataUnread(
      this.getMentionsCollection(userId, planId),
      planId,
      userId,
      callback,
      lastReadAt,
      async (lastReadAt: Date) => {
        await setDoc(d, { mentionLastReadAt: lastReadAt }, { merge: true });
      },
      false,
    );
  }

  // トークメンション未読情報の監視を停止
  stopTalkMentionsUnreadObserve(): void {
    if (this.talkMentionsUnreadUnsubscribe) {
      this.talkMentionsUnreadUnsubscribe();
      this.talkMentionsUnreadUnsubscribe = undefined;
    }
  }

  // トーク一覧のcollectionを取得
  getTalksCollection(planId: string, talkRoomId: string) {
    const { $firestore } = useNuxtApp();
    return collection(
      $firestore,
      baseCollectionName,
      planId,
      talkRoomCollectionName,
      talkRoomId,
      talkCollectionName,
    ).withConverter(TalkConverter);
  }

  // トークのdocumentを取得
  getTalkDocument(planId: string, talkRoomId: string, talkId: string) {
    const { $firestore } = useNuxtApp();
    return doc(
      $firestore,
      baseCollectionName,
      planId,
      talkRoomCollectionName,
      talkRoomId,
      talkCollectionName,
      talkId,
    ).withConverter(TalkConverter);
  }

  // スレッド一覧のcollectionを取得
  getThreadsCollection(userId: string, planId: string) {
    const { $firestore } = useNuxtApp();
    return collection(
      $firestore,
      userCollectionName,
      userId,
      planCollectionName,
      planId,
      talkThreadCollectionName,
    ).withConverter(TalkUserDataKeysConverter);
  }

  // ブックマーク一覧のcollectionを取得
  getBookmarksCollection(userId: string, planId: string) {
    const { $firestore } = useNuxtApp();
    return collection(
      $firestore,
      userCollectionName,
      userId,
      planCollectionName,
      planId,
      talkBookmarkCollectionName,
    ).withConverter(TalkUserDataKeysConverter);
  }

  // メンション一覧のcollectionを取得
  getMentionsCollection(userId: string, planId: string) {
    const { $firestore } = useNuxtApp();
    return collection(
      $firestore,
      userCollectionName,
      userId,
      planCollectionName,
      planId,
      talkMentionCollectionName,
    ).withConverter(TalkUserDataKeysConverter);
  }

  // ユーザー一覧のcollectionを取得
  getUsersCollection(planId: string) {
    const { $firestore } = useNuxtApp();
    return collection($firestore, baseCollectionName, planId, userCollectionName).withConverter(TalkRoomUserConverter);
  }

  // ユーザー別いいね一覧のcollectionを取得
  getUserGoodCollectionQuery(userId: string, talkRoomId: string) {
    const { $firestore } = useNuxtApp();
    return collection(
      $firestore,
      userCollectionName,
      userId,
      talkRoomCollectionName,
      talkRoomId,
      userGoodCollectionName,
    );
  }

  // トークデータ取得用のクエリを取得
  getTalksQuery(
    planId: string,
    talkRoomId: string,
    dateFrom: Date,
    filters: QueryFilterConstraint[] = [],
    nonFilters: QueryNonFilterConstraint[] = [],
  ) {
    if (!featureTalkThread()) {
      // スレッド機能が無効な場合は、複合インデックスを使用しないクエリを返す
      return query(this.getTalksCollection(planId, talkRoomId), and(...filters), ...nonFilters);
    }

    // 複合インデックスで TalkId は必須指定なため、 filters が空の場合は追加しておく
    const filtersToComposite = filters.length === 0 ? [where("TalkId", ">=", 0)] : filters;

    // デフォルトの表示用フィルタ（複合インデックス使用）とパラメータ指定された filters を結合
    const compositeFilter = and(
      or(where("deleteType", "==", 0), where("threadComments", ">", 0)), // 未削除 or スレッドコメントありに絞る（削除済みでも配下のスレッドが残っていたら表示する）
      where("showInRoom", "==", true), // ルーム直接表示のみにする
      where("InsertDate", ">=", Timestamp.fromDate(dateFrom)),
      ...filtersToComposite,
    );

    return query(this.getTalksCollection(planId, talkRoomId), compositeFilter, ...nonFilters);
  }

  // トーク監視
  startTalksObserve(
    planId: string,
    talkRoomId: string,
    dateFrom: Date,
    addCallback: talkObserverType,
    changeCallback: talkObserverType,
    fromTalkId: string,
    toTalkId: string,
    initDone: Function,
  ): void {
    if (!ENABLE_ON_SNAPSHOT) {
      return;
    }
    this.stopTalksObserve();

    // 指定範囲のルーム内トークを監視する
    const q = this.getTalksQuery(planId, talkRoomId, dateFrom, [
      where("TalkId", ">=", Number(fromTalkId)),
      where("TalkId", "<=", Number(toTalkId)),
    ]);
    this.talksUnsubscribe = onSnapshot(
      q,
      async snapshot => {
        for (const change of snapshot.docChanges()) {
          if (change.type === "added") {
            addCallback(this.convertTalk(change.doc.data(), talkRoomId));
          } else if (change.type === "modified") {
            // 変更コールバックをデバウンスさせる
            // いいねが多発すると変更コールバックが大量に発生してパフォーマンスに影響が出るため
            // https://fincs.backlog.com/view/FINCS-4274
            const talk = this.convertTalk(change.doc.data(), talkRoomId);
            let debounceChangeCallback = this.debounceChangeCallbacks.get(talk.talkId);
            if (!debounceChangeCallback) {
              debounceChangeCallback = _.debounce(changeCallback, TALK_UPDATE_DEBOUNCE_TIME);
              this.debounceChangeCallbacks.set(talk.talkId, debounceChangeCallback);
            }
            debounceChangeCallback(talk);
          } else if (change.type === "removed") {
            // removed の場合は change.doc が論理削除前のデータなので、最新のドキュメントを改めて取得
            const newDoc = await this.getDocument(change.doc.ref);
            if (!newDoc.exists()) {
              // 仕様上、物理削除はありえないのでこのルートは通らないはず
              sentryErrorLog(`startTalksObserve onSnapshot: ${change.doc.id} not found`);
              return;
            }
            // 論理削除済みデータに変更させる
            changeCallback(this.convertTalk(newDoc.data(), talkRoomId));
          }
        }
      },
      (error: FirestoreError) => {
        consoleWarn(`startTalksObserve onSnapshot: ${error.message}`, error);
      },
    );

    initDone();
  }

  // トーク監視停止
  stopTalksObserve() {
    if (this.talksUnsubscribe) {
      this.talksUnsubscribe();
      this.talksUnsubscribe = undefined;
    }
    // 変更コールバックのデバウンス管理をクリア
    this.debounceChangeCallbacks.clear();
  }

  startThreadTalksObserve(
    planId: string,
    talkRoomId: string,
    threadTalkId: string,
    dateFrom: Date,
    addCallback: threadTalkObserverType,
    changeCallback: threadTalkObserverType,
  ): void {
    const q = query(
      this.getTalksCollection(planId, talkRoomId),
      where("deleteType", "==", 0), // 削除データを除外
      where("threadTalkId", "==", Number(threadTalkId)), // 指定スレッドトーク配下のみ
      where("InsertDate", ">=", Timestamp.fromDate(dateFrom)),
      orderBy("TalkId", "asc"),
    );

    this.threadTalksUnsubscribe = onSnapshot(
      q,
      async snapshot => {
        for (const change of snapshot.docChanges()) {
          if (change.type === "added") {
            addCallback(this.convertTalk(change.doc.data(), talkRoomId));
          } else if (change.type === "modified") {
            changeCallback(this.convertTalk(change.doc.data(), talkRoomId));
          } else if (change.type === "removed") {
            // removed の場合は change.doc が論理削除前のデータなので、最新のドキュメントを改めて取得
            const newDoc = await this.getDocument(change.doc.ref);
            if (!newDoc.exists()) {
              // 仕様上、物理削除はありえないのでこのルートは通らないはず
              sentryErrorLog(`startThreadTalksObserve onSnapshot: ${change.doc.id} not found`);
              return;
            }
            // 論理削除済みデータに変更させる
            changeCallback(this.convertTalk(newDoc.data(), talkRoomId));
          }
        }
      },
      (error: FirestoreError) => {
        consoleWarn(`startThreadTalksObserve add onSnapshot: ${error.message}`, error);
      },
    );
  }

  stopThreadTalksObserve(): void {
    if (this.threadTalksUnsubscribe) {
      this.threadTalksUnsubscribe();
      this.threadTalksUnsubscribe = undefined;
    }
  }

  // Firestoreから document 単体取得 (キャッシュ有効時はキャッシュから取得)
  async getDocument(docRef: DocumentReference) {
    return featureFirestoreCache() ? await getFirestoreDocFromCacheOrServer(docRef) : await getFirestoreDoc(docRef);
  }

  // Firestoreから document リスト取得 (キャッシュ有効時はキャッシュから取得)
  async getDocuments(query: Query, useCacheIfAvailable: boolean) {
    return useCacheIfAvailable && featureFirestoreCache()
      ? await getFirestoreDocsFromCacheOrServer(query)
      : await getFirestoreDocs(query);
  }

  // トークIDを基準にしてトーク一覧を取得する
  //
  // 想定する使い方は下記の通り。
  // - 1. 初回読み込み: talkId=undefined, isBefore=true, includeSelf=false
  // - 2. 上無限スクロール: talkId=先頭トークID, isBefore=true, includeSelf=false
  // - 3. 下無限スクロール: talkId=末尾トークID, isBefore=false, includeSelf=false
  // - 4. ダイレクトジャンプ: talkId=対象トークID, isBefore=true, includeSelf=false + talkId=対象トークID, isBefore=false, includeSelf=true (2回呼んで連結する。その後は上下無限スクロールできる)
  //
  // 通常時はトークルームオープン時に1で初回読み込みを行い、その後は上無限スクロール時に2を行う
  // 返信元や検索結果のトークへダイレクトジャンプする際は4で対象トーク＋前後のトーク一覧へ差し替え、その後は上下の無限スクロール時に2,3を行う
  async getTalks(
    planId: string,
    talkRoomId: string,
    talkId: string | undefined,
    dateFrom: Date,
    isBefore: boolean,
    includeSelf: boolean,
    pageSize: number,
    useCacheIfAvailable: boolean,
  ): Promise<Talk[]> {
    // トークID未指定時は、includeSelf指定不可
    if (!talkId && includeSelf) {
      throw new Error("TalkRoomRepository.getTalks: talkId is required when includeSelf is true");
    }

    // 返すデータに論理削除が多く含まれるとスクロール動作が正常に動かない
    // よって論理削除を含まずに10個以上返すように繰り返す
    let resultTalks: Talk[] = [];
    for (let i = 0; ; i++) {
      const talks = await this.getTalksOnce(
        planId,
        talkRoomId,
        talkId,
        dateFrom,
        isBefore,
        includeSelf,
        pageSize,
        useCacheIfAvailable,
      );

      const activeTalks = talks.filter(talk => talk.canView());
      resultTalks = isBefore ? talks.concat(resultTalks) : resultTalks.concat(talks);
      const resultActiveTalks = resultTalks.filter(talk => talk.canView());
      // 論理削除されてないトークが 10 個以上蓄積するか、
      // 論理削除がひとつも含まれなくなったら終了
      if (resultActiveTalks.length >= 10 || talks.length === activeTalks.length) {
        break;
      } else if (i === 5) {
        // 無限ループを避けるため、5回で終わらなかったらエラーログを出して終了
        sentryErrorLog(`getTalks: too many talkRoomId=${talkRoomId}`);
        break;
      }
      // 基準トークIDを更新して繰り返す
      talkId = isBefore ? talks.at(0)!.talkId : talks.at(-1)!.talkId;
    }
    return this.convertTalks(resultTalks, talkRoomId);
  }

  async getThreadChildTalks(
    planId: string,
    talkRoomId: string,
    threadTalkId: string | undefined,
    dateFrom: Date,
    latestCount: number,
  ): Promise<Talk[]> {
    const q = query(
      this.getTalksCollection(planId, talkRoomId),
      where("deleteType", "==", 0), // 削除データを除外
      where("threadTalkId", "==", Number(threadTalkId)), // 指定スレッドトーク配下のみ
      where("InsertDate", ">=", Timestamp.fromDate(dateFrom)),
      orderBy("TalkId", "desc"), // 直近 latestCount 件取得のために降順
      limit(latestCount),
    );
    const querySnapshot = await this.getDocuments(q, false);
    const resultTalks = querySnapshot.docs.map(doc => doc.data()).reverse(); // TalkId 昇順で返す
    return this.convertTalks(resultTalks, talkRoomId);
  }

  async getTalksOnce(
    planId: string,
    talkRoomId: string,
    talkId: string | undefined,
    dateFrom: Date,
    isBefore: boolean,
    includeSelf: boolean,
    pageSize: number,
    useCacheIfAvailable: boolean,
  ): Promise<Talk[]> {
    const nonFilters = talkId
      ? [
          orderBy("TalkId", isBefore ? "desc" : "asc"),
          includeSelf ? startAt(Number(talkId)) : startAfter(Number(talkId)),
          limit(pageSize),
        ]
      : [orderBy("TalkId", isBefore ? "desc" : "asc"), limit(pageSize)];
    const snapshot = await this.getDocuments(
      this.getTalksQuery(planId, talkRoomId, dateFrom, [], nonFilters),
      useCacheIfAvailable,
    );

    // トークID昇順リストで返す
    const list: Talk[] = [];
    snapshot.forEach(doc => {
      list.push(this.convertTalk(doc.data(), talkRoomId));
    });

    return isBefore ? list.reverse() : list;
  }

  // トーク取得
  async getTalk(planId: string, talkRoomId: string, talkId: string): Promise<Talk | undefined> {
    const doc = this.getTalkDocument(planId, talkRoomId, talkId);
    const snap = await this.getDocument(doc);
    if (!snap.exists()) {
      return undefined;
    }
    return this.convertTalk(snap.data(), talkRoomId);
  }

  // 次トークID取得
  async getNextTalkId(planId: string, talkRoomId: string, talkId: string, dateFrom: Date): Promise<string | undefined> {
    const talks = await this.getTalksOnce(planId, talkRoomId, talkId, dateFrom, false, false, 1, false);
    if (!talks || talks.length === 0) {
      return undefined;
    }
    return talks[0].talkId;
  }

  // トークデータ返却前に変換する
  convertTalk(talk: Talk, talkRoomId: string) {
    // Firestore 内のトークデータにルーム情報がないのでここで付与（メンションリストからの移動などで使う）
    talk.talkRoomId = talkRoomId;
    return talk;
  }

  // トークリスト返却前に変換する
  convertTalks(talks: Talk[], talkRoomId: string) {
    for (const talk of talks) {
      this.convertTalk(talk, talkRoomId);
    }
    return talks;
  }

  // ユーザー監視
  startUsersObserve(planId: string, addCallback: userObserverType, changeCallback: userObserverType): void {
    if (!ENABLE_ON_SNAPSHOT) {
      return;
    }
    this.stopUsersObserve();
    const collection = this.getUsersCollection(planId);

    // 監視の作成
    this.usersChangeUnsubscribe = onSnapshot(
      query(collection, where("UpdateDate", ">", Timestamp.now())),
      snapshot => {
        snapshot.docChanges().forEach(change => {
          // 追加時
          if (change.type === "added") {
            addCallback(change.doc.data());
          }
          // 変更時
          if (change.type === "modified") {
            changeCallback(change.doc.data());
          }
        });
      },
      (error: FirestoreError) => {
        consoleWarn(`startUsersObserve onSnapshot: ${error.message}`, error);
      },
    );
  }

  // ユーザー監視停止
  stopUsersObserve() {
    if (this.usersAddUnsubscribe) {
      this.usersAddUnsubscribe();
      this.usersAddUnsubscribe = undefined;
    }
    if (this.usersChangeUnsubscribe) {
      this.usersChangeUnsubscribe();
      this.usersChangeUnsubscribe = undefined;
    }
  }

  // ユーザー監視
  startUserGoodsObserve(userId: string, talkRoomId: string, callBack: userGoodObserverType): void {
    if (!ENABLE_ON_SNAPSHOT) {
      return;
    }
    this.stopUserGoodsObserve();
    const collection = this.getUserGoodCollectionQuery(userId, talkRoomId);

    // 監視の作成
    this.userGoodsUnsubscribe = onSnapshot(
      query(collection),
      snapshot => {
        snapshot.docChanges().forEach(change => {
          callBack(change.type, change.doc.id);
        });
      },
      (error: FirestoreError) => {
        consoleWarn(`startUserGoodsObserve onSnapshot: ${error.message}`, error);
      },
    );
  }

  // ユーザー監視停止
  stopUserGoodsObserve() {
    if (this.userGoodsUnsubscribe) {
      this.userGoodsUnsubscribe();
      this.userGoodsUnsubscribe = undefined;
    }
  }

  // ユーザー一覧取得 (ユーザーIDリストで引当)
  async getUsers(planId: string, userIds: string[]): Promise<TalkRoomUser[]> {
    // userIds をユニークにさせる
    const uniqUserIds = Array.from(new Set(userIds));

    // firestore 仕様で where in で指定可能なのは 30 個までなので分割実行
    let list: TalkRoomUser[] = [];
    for (let i = 0; i < uniqUserIds.length; i += FIRESTORE_WHERE_IN_LIMIT) {
      const slicedUserIds = uniqUserIds.slice(i, i + FIRESTORE_WHERE_IN_LIMIT);
      const querySnapshot = await this.getDocuments(
        query(this.getUsersCollection(planId), where(documentId(), "in", slicedUserIds)),
        true,
      );
      list = list.concat(querySnapshot.docs.map(doc => doc.data()));
    }
    return list;
  }

  async getTalkUserDataList(
    collection: CollectionReference<TalkUserDataKeys>,
    planId: string,
    userId: string,
    lastCreatedAt: Date | undefined,
    count: number,
    showThreads = false,
  ): Promise<TalkUserData[]> {
    const q = lastCreatedAt
      ? query(collection, orderBy("createdAt", "desc"), limit(count), startAfter(Timestamp.fromDate(lastCreatedAt)))
      : query(collection, orderBy("createdAt", "desc"), limit(count));
    const snapshot = await this.getDocuments(q, false);
    if (snapshot.empty) {
      return [];
    }
    const userDataList: TalkUserDataKeys[] = snapshot.docs.map(doc => doc.data());
    return await this.batchConvertToTalkUserData(planId, userId, userDataList, showThreads);
  }

  async getTalkThreads(
    planId: string,
    userId: string,
    lastCreatedAt: Date | undefined,
    count: number,
  ): Promise<TalkUserData[]> {
    return await this.getTalkUserDataList(
      this.getThreadsCollection(userId, planId),
      planId,
      userId,
      lastCreatedAt,
      count,
      true,
    );
  }

  async getTalkBookmarks(
    planId: string,
    userId: string,
    lastCreatedAt: Date | undefined,
    count: number,
  ): Promise<TalkUserData[]> {
    return await this.getTalkUserDataList(
      this.getBookmarksCollection(userId, planId),
      planId,
      userId,
      lastCreatedAt,
      count,
    );
  }

  async getTalkBookmarkKeysByRoomId(planId: string, userId: string, talkRoomId: string): Promise<TalkUserDataKeys[]> {
    const collection = this.getBookmarksCollection(userId, planId);
    const q = query(collection, where("talkRoomId", "==", Number(talkRoomId)));
    const snapshot = await this.getDocuments(q, false);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  async getTalkMentions(
    planId: string,
    userId: string,
    lastCreatedAt: Date | undefined,
    count: number,
  ): Promise<TalkUserData[]> {
    return await this.getTalkUserDataList(
      this.getMentionsCollection(userId, planId),
      planId,
      userId,
      lastCreatedAt,
      count,
    );
  }

  // ルームが混在する userDataKeysList をトークユーザーデータリストへバッチ変換
  async batchConvertToTalkUserData(
    planId: string,
    userId: string,
    userDataKeysList: TalkUserDataKeys[],
    showThreads: boolean,
  ): Promise<TalkUserData[]> {
    // userDataKeysList をルームごとに分割
    const userDataKeysListGroupByRoom: Record<string, TalkUserDataKeys[]> = {};
    const talkRoomIds = []; // ソート用にトークルームIDをリスト保持
    for (const userDataKeys of userDataKeysList) {
      if (!(userDataKeys.talkRoomId in userDataKeysListGroupByRoom)) {
        talkRoomIds.push(userDataKeys.talkRoomId);
        userDataKeysListGroupByRoom[userDataKeys.talkRoomId] = [];
      }
      userDataKeysListGroupByRoom[userDataKeys.talkRoomId].push(userDataKeys);
    }
    talkRoomIds.sort((a, b) => Number(a) - Number(b)); // トークルームIDでソート

    // ルームごとにトーク情報をバッチ取得
    const list: TalkUserData[] = [];
    for (const talkRoomId of talkRoomIds) {
      const userDataKeysListByRoom: TalkUserDataKeys[] = userDataKeysListGroupByRoom[talkRoomId];
      const userDataList = await this.batchConvertToTalkUserDataByRoom(
        planId,
        userId,
        talkRoomId,
        userDataKeysListByRoom,
        showThreads,
      );
      list.push(...userDataList);
    }

    // 最後に作成日時でソートして返す
    return list.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  // 特定ルームの userDataKeysList をトークユーザーデータリストへバッチ変換
  async batchConvertToTalkUserDataByRoom(
    planId: string,
    userId: string,
    talkRoomId: string,
    userDataKeysList: TalkUserDataKeys[],
    showThreads: boolean,
  ) {
    if (userDataKeysList.length === 0) {
      throw new Error(
        `Error batchConvertToTalkUserDataByRoom: list must not be empty: ${planId}/${userId}/${talkRoomId} ${userDataKeysList}`,
      );
    }

    // where in で取れるのは 30 個までなので、それ以上はエラーを出す
    // 実際には最大でも 10 個のはず
    if (userDataKeysList.length > FIRESTORE_WHERE_IN_LIMIT) {
      throw new Error(
        `Error batchConvertToTalkUserDataByRoom: list must be ${FIRESTORE_WHERE_IN_LIMIT} or less: ${planId}/${userId}/${talkRoomId} ${userDataKeysList}`,
      );
    }

    // 次のトークバッチ取得と照合させるため、userDataKeysList をトークID降順でソートしておく
    userDataKeysList.sort((a: any, b: any) => Number(b.talkId) - Number(a.talkId));

    // トークデータをバッチ取得
    const querySnapshot = await this.getDocuments(
      query(
        this.getTalksCollection(planId, talkRoomId),
        where(
          documentId(),
          "in",
          userDataKeysList.map(userDataKeys => userDataKeys.talkId),
        ),
        orderBy("TalkId", "desc"),
      ),
      false, // キャッシュあると不安定なので外す
    );
    const talks = querySnapshot.docs.map(doc => doc.data());
    if (!talks.length) {
      throw new Error(
        `Error batchConvertToTalkUserDataByRoom: talk not found: ${planId}/${userId}/${talkRoomId} ${userDataKeysList} ${talks}`,
      );
    }
    if (talks.length !== userDataKeysList.length) {
      throw new Error(
        `Error batchConvertToTalkUserDataByRoom: some talks are missing: ${planId}/${userId}/${talkRoomId} ${userDataKeysList} ${talks}`,
      );
    }

    // ルーム既読状態を確認
    const lastReadTalkIdOfRoom = (await this.getLastRead(userId, talkRoomId)) || 0;

    // トークユーザーデータに変換して返す
    const list: TalkUserData[] = [];
    for (const i in userDataKeysList) {
      const talk = talks[i];
      talk.talkRoomId = talkRoomId;

      let hasUnread = false;
      if (showThreads) {
        // スレッドタブの場合
        // スレッドの最終既読トークIDを採用
        const lastReadTalkId = (await this.getThreadLastRead(userId, talkRoomId, talk.talkId)) || 0;
        // talk.threadLatestTalkId と比較して未読判定
        hasUnread = lastReadTalkId < Number(talk.threadLatestTalkId);
      } else {
        // スレッドタブ以外の場合
        // スレッド内トークかつルーム非表示の場合、スレッドの最終既読トークIDを採用
        // 上記以外の場合、ルームの最終既読トークIDを採用
        const lastReadTalkId =
          talk.isThreadChild() && !talk.showInRoom
            ? (await this.getThreadLastRead(userId, talkRoomId, talk.threadTalkId)) || 0
            : lastReadTalkIdOfRoom;
        // talk.talkId と比較して未読判定
        hasUnread = lastReadTalkId < Number(talk.talkId);
      }
      list.push(new TalkUserData(talks[i], userDataKeysList[i].createdAt, hasUnread));
    }
    return list;
  }

  // 特定の userDataKeys をトークユーザーデータへ変換
  async convertToTalkUserData(planId: string, userId: string, userDataKeys: TalkUserDataKeys, showThreads: boolean) {
    const talkRoomId = userDataKeys.talkRoomId;
    const list = await this.batchConvertToTalkUserDataByRoom(planId, userId, talkRoomId, [userDataKeys], showThreads);
    if (!list.length) {
      throw new Error(`Error convertToTalkUserData: talk not found: ${planId}/${userId}/${talkRoomId}`);
    }
    return list[0];
  }

  // 既読管理用docの取得
  getLastReadDocQuery(userId: string, talkRoomId: string) {
    const { $firestore } = useNuxtApp();
    return doc($firestore, userCollectionName, userId, talkRoomCollectionName, talkRoomId).withConverter(
      TalkLastReadConverter,
    );
  }

  // 既読取得
  async getLastRead(userId: string, talkRoomId: string): Promise<number | undefined> {
    try {
      const doc = this.getLastReadDocQuery(userId, talkRoomId);
      const snap = await getFirestoreDoc(doc);
      return snap.exists() ? snap.data().lastReadTalkId : undefined;
    } catch (error: any) {
      throw new Error("Error get lastReadTalkId: " + error);
    }
  }

  // 既読設定
  async setLastRead(userId: string, talkRoomId: string, talkId: string): Promise<boolean> {
    try {
      const talkIdNumber = Number(talkId);
      if (!talkIdNumber) {
        throw new Error(`Error set lastReadTalkId: invalid talkId: ${talkId}`);
      }
      const doc = this.getLastReadDocQuery(userId, talkRoomId);
      const snap = await getFirestoreDoc(doc);
      if (snap.exists()) {
        if (talkIdNumber <= snap.data().lastReadTalkId) {
          // サーバー上にある既読talkId以下の場合は更新しない
          return false;
        }
      }
      // 更新
      await setDoc(doc, new TalkLastRead(talkRoomId, talkIdNumber));
      return true;
    } catch (error: any) {
      throw new Error("Error set lastReadTalkId: " + error);
    }
  }

  // スレッド既読管理用docの取得
  getThreadLastReadDocQuery(userId: string, talkRoomId: string, threadTalkId: string) {
    const { $firestore } = useNuxtApp();
    return doc(
      $firestore,
      userCollectionName,
      userId,
      talkRoomCollectionName,
      talkRoomId,
      talkCollectionName,
      threadTalkId,
    ).withConverter(TalkLastReadConverter);
  }

  // スレッド既読取得
  async getThreadLastRead(userId: string, talkRoomId: string, threadTalkId: string): Promise<number | undefined> {
    try {
      const doc = this.getThreadLastReadDocQuery(userId, talkRoomId, threadTalkId);
      const snap = await getFirestoreDoc(doc);
      return snap.exists() ? snap.data().lastReadTalkId : undefined;
    } catch (error: any) {
      throw new Error("Error get lastThreadReadTalkId: " + error);
    }
  }

  // スレッド既読設定
  async setThreadLastRead(userId: string, talkRoomId: string, threadTalkId: string, talkId: string): Promise<boolean> {
    try {
      const talkIdNumber = Number(talkId);
      if (!talkIdNumber) {
        throw new Error(`Error set lastThreadReadTalkId: invalid talkId: ${talkId}`);
      }
      const doc = this.getThreadLastReadDocQuery(userId, talkRoomId, threadTalkId);
      const snap = await getFirestoreDoc(doc);
      if (snap.exists()) {
        if (talkIdNumber <= snap.data().lastReadTalkId) {
          // サーバー上にある既読talkId以下の場合は更新しない
          return false;
        }
      }
      // 更新
      await setDoc(doc, new TalkLastRead(talkRoomId, talkIdNumber));
      return true;
    } catch (error: any) {
      throw new Error("Error set lastThreadReadTalkId: " + error);
    }
  }

  // いいね管理用collectionの取得
  getGoodCollectionQuery(planId: string, talkRoomId: string, talkId: string) {
    const { $firestore } = useNuxtApp();
    return collection(
      $firestore,
      baseCollectionName,
      planId,
      talkRoomCollectionName,
      talkRoomId,
      talkCollectionName,
      talkId,
      favoriteCollectionName,
    );
  }

  // いいね管理用docの取得
  getGoodDocQuery(planId: string, talkRoomId: string, talkId: string, userId: string) {
    const { $firestore } = useNuxtApp();
    return doc(
      $firestore,
      baseCollectionName,
      planId,
      talkRoomCollectionName,
      talkRoomId,
      talkCollectionName,
      talkId,
      favoriteCollectionName,
      userId,
    );
  }

  // ユーザー別いいね一覧のcollectionを取得
  getUserGoodDocQuery(userId: string, talkRoomId: string, talkId: string) {
    const { $firestore } = useNuxtApp();
    return doc(
      $firestore,
      userCollectionName,
      userId,
      talkRoomCollectionName,
      talkRoomId,
      userGoodCollectionName,
      talkId,
    );
  }

  // いいねを送る
  async sendGood(planId: string, talkRoomId: string, talkId: string, userId: string): Promise<void> {
    try {
      const userGoodDoc = this.getUserGoodDocQuery(userId, talkRoomId, talkId);
      const userGoodSnap = await getFirestoreDoc(userGoodDoc);

      const goodDoc = this.getGoodDocQuery(planId, talkRoomId, talkId, userId);
      const goodSnap = await getFirestoreDoc(goodDoc);

      if (!goodSnap.exists() && !userGoodSnap.exists()) {
        // ユーザー別いいねデータ追加
        await setDoc(userGoodDoc, { createdAt: serverTimestamp() });

        // いいねデータ追加
        await setDoc(goodDoc, { createdAt: serverTimestamp() });

        const talkDoc = this.getTalkDocument(planId, talkRoomId, talkId);
        await updateDoc(talkDoc, { GoodCount: increment(1) });
      }
    } catch (error: any) {
      throw new Error("Error sendGood: " + error);
    }
  }

  // いいねを削除
  async deleteGood(planId: string, talkRoomId: string, talkId: string, userId: string): Promise<void> {
    try {
      const userGoodDoc = this.getUserGoodDocQuery(userId, talkRoomId, talkId);
      const userGoodSnap = await getFirestoreDoc(userGoodDoc);

      const goodDoc = this.getGoodDocQuery(planId, talkRoomId, talkId, userId);
      const goodSnap = await getFirestoreDoc(goodDoc);

      if (goodSnap.exists() && userGoodSnap.exists()) {
        // ユーザー別いいねデータ削除
        await deleteDoc(userGoodDoc);

        // いいねデータ削除
        await deleteDoc(goodDoc);

        const talkDoc = this.getTalkDocument(planId, talkRoomId, talkId);
        await updateDoc(talkDoc, { GoodCount: increment(-1) });
      }
    } catch (error: any) {
      throw new Error("Error deleteGood: " + error);
    }
  }

  // いいね変更通知用doc
  getGoodNotificationDocQuery(planId: string, talkRoomId: string, talkId: string) {
    const { $firestore } = useNuxtApp();
    return doc($firestore, baseCollectionName, planId, talkRoomCollectionName, talkRoomId, talkCollectionName, talkId);
  }

  // いいね変更通知用doc
  async sendGoodNotification(planId: string, talkRoomId: string, talkId: string): Promise<void> {
    try {
      const doc = this.getGoodNotificationDocQuery(planId, talkRoomId, talkId);
      const snap = await getFirestoreDoc(doc);
      if (snap.exists()) {
        // いいね更新時間を更新する
        await updateDoc(doc, { favoriteUpdatedAt: serverTimestamp() });
        // await setDoc(doc, {favoriteUpdatedAt: serverTimestamp()}, { merge: true });
      }
    } catch (error: any) {
      throw new Error("Error sendGood: " + error);
    }
  }

  // トークルーム未読状態取得
  async getTalkRoomUnreads(
    userId: string,
    planId: string,
    talkRoomIds: string[],
    quitOnFirstUnread: boolean,
  ): Promise<Record<string, boolean>> {
    const startTime = performance.now();

    // 未読状態を保持
    const unreadMap: Record<string, boolean> = {};

    // 分割したルームIDリストの未読状態を設定。後ほど、並列実行で呼び出す
    const setSlicedTalkRoomUnreads = async (slicedTalkRoomIds: string[]) => {
      // トークルームの最新トークIDを取得
      let roomSnap: QuerySnapshot<any, DocumentData> | undefined;
      const getRoomFunc = async () => {
        roomSnap = await this.getDocuments(
          query(this.getTalkRoomCollection(planId), where(documentId(), "in", slicedTalkRoomIds)),
          false,
        );
      };

      // ユーザーのルームごと既読トークIDを取得
      let userRoomSnap: QuerySnapshot<any, DocumentData> | undefined;
      const getUserRoomFunc = async () => {
        userRoomSnap = await this.getDocuments(
          query(this.getUserTalkRoomCollection(userId), where(documentId(), "in", slicedTalkRoomIds)),
          false,
        );
      };

      // 上記2点の取得も並列実行する
      await Promise.all([getRoomFunc(), getUserRoomFunc()]);

      // 上記2点いずれかの取得結果が空の場合は未読なし
      if (!roomSnap || roomSnap.empty) {
        return false;
      }
      if (!userRoomSnap || userRoomSnap.empty) {
        return false;
      }

      // 未読ありフラグ
      let hasUnread = false;

      // 未読判定結果を設定
      roomSnap.docs.forEach(doc => {
        // ルームの最新トークID
        const latestTalkId = doc.data().LatesteTalkID || 0;
        // ユーザーのルームごと既読トークID
        const lastReadTalkId = (userRoomSnap!.docs.find(userDoc => userDoc.id === doc.id)?.data().lastReadTalkId ||
          0) as number;
        // 上記を比較して未読判定
        unreadMap[doc.id] = lastReadTalkId < latestTalkId;
        if (lastReadTalkId < latestTalkId) {
          hasUnread = true; // ひとつでも未読があれば true
        }
      });

      return hasUnread;
    };

    // 並列実行用パラメータ
    const batchSize = FIRESTORE_WHERE_IN_LIMIT;
    const totalBatches = Math.ceil(talkRoomIds.length / batchSize);
    const maxConcurrency = 20; // 同時実行数の制限。ほぼ I/O 待ちなので多めに設定
    let i = 0; // worker 間で共有するので workerFunc の外側に定義すること

    // talkRoomIds を分割して未読状態を取得する関数
    // 並列実行するため、i のインクリメントは await 前に行うこと
    const workerFunc = async () => {
      while (i < talkRoomIds.length) {
        // ルームIDリストを分割処理
        // consoleLog(`TalkRoomRepository.getTalkRoomUnreads: workerFunc ${i}`);
        const slicedTalkRoomIds = talkRoomIds.slice(i, i + batchSize);
        slicedTalkRoomIds.forEach(talkRoomId => {
          unreadMap[talkRoomId] = false; // 初期化
        });
        i += batchSize; // await 前にインクリメント必須

        // 分割したルームIDリストの未読状態を設定
        const hasUnread = await setSlicedTalkRoomUnreads(slicedTalkRoomIds);
        if (hasUnread && quitOnFirstUnread) {
          // quitOnFirstUnread=true 指定の場合、ひとつでも未読があれば以降のルームIDは無視して終了
          i = talkRoomIds.length;
          break;
        }
      }
    };

    // 並列実行
    const workers = Array.from({ length: Math.min(maxConcurrency, totalBatches) }, () => workerFunc());
    await Promise.all(workers);

    // 重い処理なので処理時間を計測し、今後のチューニングの参考に
    const endTime = performance.now();
    consoleLog(
      `TalkRoomRepository.getTalkRoomUnreads: ${talkRoomIds.length} rooms processed in ${endTime - startTime} ms`,
    );

    return unreadMap;
  }

  async getDmLastReadAt(userId: string, planId: string): Promise<Date> {
    const d = this.getUserPlanDocument(userId, planId);
    const snap = await getFirestoreDoc(d);
    const lastReadAtDefault = new Date("2023-01-01T00:00:00Z");
    if (!snap.exists()) {
      return lastReadAtDefault;
    }
    return snap.data().dmLastReadAt ? snap.data().dmLastReadAt.toDate() : lastReadAtDefault;
  }

  async setDmLastReadAt(userId: string, planId: string, date: Date): Promise<void> {
    const d = this.getUserPlanDocument(userId, planId);
    await setDoc(d, { dmLastReadAt: date }, { merge: true });
  }

  // api

  async getTalkRooms(planId: string, excludeDm: boolean): Promise<TalkRoom[]> {
    const paramsObj: { [name: string]: string } = {
      page: "1",
      page_size: TALK_ROOM_TOTAL_MAX.toString(),
      excludeDm: excludeDm ? "true" : "false",
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<{ statusCode: number; talkRooms: [] }>(
      `/talk/room/${planId}/?` + searchParams.toString(),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.getTalkRooms API Error: ${JSON.stringify(res)}`);
    }

    // TODO 暫定でトーク未読フラグを無視するように
    // https://fincs.backlog.com/view/FINCS-3904
    // ↓↓↓ BE 側にて未読フラグが設定されなくなったらこのコードは削除する
    for (const i in res.talkRooms) {
      (res.talkRooms[i] as TalkRoom).hasUnread = false;
    }
    // ↑↑↑ BE 側にて未読フラグが設定されなくなったらこのコードは削除する

    return res.talkRooms;
  }

  convertTalkFromApi(apiTalk: Record<string, string>): Talk {
    const talk = new Talk(apiTalk.talkId, apiTalk);
    talk.postDateTime = new Date(apiTalk.postDateTime); // string -> Date 変換
    return talk;
  }

  async searchTalksByPlan(planId: string, searchWord: string, countMax: number): Promise<Talk[]> {
    const paramsObj: { [name: string]: string } = {
      plan_id: planId,
      search_word: searchWord,
      latest_days: TALK_SEARCH_PERIOD_DAYS.toString(),
      sort: "desc",
      page_size: String(countMax),
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<{ statusCode: number; talks: [] }>(`/talk/?` + searchParams.toString());
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.searchTalksByPlan API Error: ${JSON.stringify(res)}`);
    }
    return res.talks.map(t => this.convertTalkFromApi(t));
  }

  async searchTalksByTalkRoom(talkRoomId: string, searchWord: string, countMax: number): Promise<Talk[]> {
    const paramsObj: { [name: string]: string } = {
      search_word: searchWord,
      latest_days: TALK_SEARCH_PERIOD_DAYS.toString(),
      sort: "desc",
      page_size: String(countMax),
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<{ statusCode: number; talks: [] }>(`/talk/${talkRoomId}/?` + searchParams.toString());
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.searchTalksByTalkRoom API Error: ${JSON.stringify(res)}`);
    }
    return res.talks.map(t => this.convertTalkFromApi(t));
  }

  async getGoodUsersFromApi(talkId: string, page: number): Promise<TalkRoomUser[]> {
    const paramsObj: { [name: string]: string } = {
      page: page.toString(),
      page_size: "100",
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<TalkGoodUserResponse>(`/talk/goodUsers/${talkId}?` + searchParams.toString());
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.getGoodUsersFromApi API Error: ${JSON.stringify(res)}`);
    }
    return res.talkGoodUsers;
  }

  async getTalkFromApi(talkRoomId: string, talkId: string): Promise<Talk | undefined> {
    const paramsObj: { [name: string]: string } = {
      talk_id: talkId,
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<{ statusCode: number; talks: Array<Talk> }>(
      `/talk/${talkRoomId}/?` + searchParams.toString(),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.getTalkFromApi API Error: ${JSON.stringify(res)}`);
    }
    return res.talks?.length ? res.talks[0] : undefined;
  }

  async sendTalk(
    talkRoomId: string,
    message: string,
    mentionUserIds: string[],
    replyTalkId: string | undefined,
    threadTalkId: string,
    showInRoom: boolean,
  ): Promise<void> {
    const res = await PostRequest<{ statusCode: number }>(
      `/talk/post/text/${talkRoomId}`,
      JSON.stringify({
        contents: message,
        mention_user_ids: mentionUserIds.length ? mentionUserIds : null, // API 仕様として空の場合は null で送る
        reply_talk_id: replyTalkId,
        thread_talk_id: threadTalkId,
        show_in_room: showInRoom,
      }),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.sendTalk API Error: ${JSON.stringify(res)}`);
    }
  }

  async getContentsUrl(fileKey: string): Promise<{ statusCode: number; presignedUrl: string }> {
    const res = await GetRequest<{ statusCode: number; presignedUrl: string }>(
      `/talk/get/file/presigned_url/${fileKey}`,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.getContentsUrl API Error: ${JSON.stringify(res)}`);
    }
    return res;
  }

  async sendFile(
    talkRoomId: string,
    file: File,
    note: string,
    mentionUserIds: string[],
    replyTalkId: string | undefined,
    threadTalkId: string,
    showInRoom: boolean,
    thumbnailBase64?: string,
  ): Promise<void> {
    const uploadFile = isImageFile(file) ? await convertTalkImage(file) : file;
    // 署名付きS3アップロードURLを取得する
    const mimeType = uploadFile.type;
    const storageType = isMovieFile(file) ? "wasabi" : "s3"; // 動画は wasabi、その他は s3 にアップロード
    const presignedRes = await GetRequest<{
      statusCode: number;
      presignedUrl: string;
      fileKey: string;
    }>(`/talk/post/file/presigned_url?mime_type=${mimeType}&storage_type=${storageType}`);
    if (!presignedRes || presignedRes.statusCode !== 200) {
      throw new Error("Failed get presigned_url");
    }

    // S3 にアップロード
    const s3Res = await PutS3Request(presignedRes.presignedUrl, uploadFile);
    if (!s3Res || s3Res.statusCode !== 200) {
      throw new Error("Failed to put s3");
    }

    if (isImageFile(uploadFile)) {
      // 画像の場合はサムネイルを作成してアップロード
      const base64File = await convertTalkThumbnail(uploadFile);

      const res = await PostRequest<{ statusCode: number }>(
        `/talk/post/image/${talkRoomId}`,
        JSON.stringify({
          file_key: presignedRes.fileKey,
          file_name: uploadFile.name,
          thumbnail_image: base64File,
          note: note || null,
          mention_user_ids: mentionUserIds.length ? mentionUserIds : null, // API 仕様として空の場合は null で送る
          reply_talk_id: replyTalkId,
          thread_talk_id: threadTalkId,
          show_in_room: showInRoom,
        }),
      );
      if (!res || res.statusCode !== 200) {
        throw new Error("Failed to post talk image");
      }
    } else if (isMovieFile(uploadFile)) {
      // 動画の場合は引数のサムネイルを渡してアップロード
      const base64File = thumbnailBase64!;

      const res = await PostRequest<{ statusCode: number }>(
        `/talk/post/movie/${talkRoomId}`,
        JSON.stringify({
          file_key: presignedRes.fileKey,
          file_name: uploadFile.name,
          thumbnail_image: base64File,
          note: note || null,
          mention_user_ids: mentionUserIds.length ? mentionUserIds : null, // API 仕様として空の場合は null で送る
          reply_talk_id: replyTalkId,
          thread_talk_id: threadTalkId,
          show_in_room: showInRoom,
        }),
      );
      if (!res || res.statusCode !== 200) {
        throw new Error("Failed to post talk movie");
      }
    } else if (isPdfFile(uploadFile)) {
      // pdfはファイルネームを付与してアップロード
      const res = await PostRequest<{ statusCode: number }>(
        `/talk/post/pdf/${talkRoomId}`,
        JSON.stringify({
          file_key: presignedRes.fileKey,
          file_name: uploadFile.name,
          note: note || null,
          mention_user_ids: mentionUserIds.length ? mentionUserIds : null, // API 仕様として空の場合は null で送る
          reply_talk_id: replyTalkId,
          thread_talk_id: threadTalkId,
          show_in_room: showInRoom,
        }),
      );
      if (!res || res.statusCode !== 200) {
        throw new Error("Failed to post talk pdf");
      }
    } else {
      // その他のファイルはエラー
      throw new Error("Invalid file type");
    }
  }

  async sendFileWithoutRoom(file: File, note: string): Promise<Talk | undefined> {
    const uploadFile = isImageFile(file) ? await convertTalkImage(file) : file;
    // 署名付きS3アップロードURLを取得する
    const mimeType = uploadFile.type;
    const presignedRes = await GetRequest<{
      statusCode: number;
      presignedUrl: string;
      fileKey: string;
    }>(`/talk/post/file/presigned_url?mime_type=${mimeType}`);
    if (!presignedRes || presignedRes.statusCode !== 200) {
      throw new Error("Failed get presigned_url");
    }

    // S3 にアップロード
    const s3Res = await PutS3Request(presignedRes.presignedUrl, uploadFile);
    if (!s3Res || s3Res.statusCode !== 200) {
      throw new Error("Failed to put s3");
    }

    if (isImageFile(uploadFile)) {
      // 画像の場合はサムネイルを作成してアップロード
      const base64File = await convertTalkThumbnail(uploadFile);
      return new Talk("", {
        fileKey: presignedRes.fileKey,
        fileName: uploadFile.name,
        thumbnailImage: base64File,
        note: note || null,
        contentsUrl: presignedRes.presignedUrl,
        messageType: "image",
      });
    } else if (isPdfFile(uploadFile)) {
      return new Talk("", {
        fileKey: presignedRes.fileKey,
        note: note || null,
        fileName: uploadFile.name,
        contentsUrl: presignedRes.presignedUrl,
        messageType: "pdf",
      });
    }
  }

  async editTalk(talkId: string, text: string, mentionUserIds: string[], showInRoom: boolean): Promise<void> {
    const res = await PutRequest<{ statusCode: number }>(
      `/talk/post/${talkId}`,
      JSON.stringify({
        text,
        mention_user_ids: mentionUserIds.length ? mentionUserIds : null, // API 仕様として空の場合は null で送る
        show_in_room: showInRoom,
      }),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`Failed update talk: response=${JSON.stringify(res)}`);
    }
  }

  async deleteTalk(talkId: string): Promise<void> {
    const res = await DeleteRequest<{ statusCode: number }>(`/talk/post/${talkId}`);
    if (!res || res.statusCode !== 200) {
      throw new Error(`Failed delete talk: response=${JSON.stringify(res)}`);
    }
  }

  async setBookmark(
    userId: string,
    planId: string,
    talkRoomId: string,
    talkId: string,
    value: boolean,
  ): Promise<boolean> {
    // ブックマーク追加前に個数チェック
    if (value) {
      const keys = await this.getTalkBookmarkKeysByRoomId(planId, userId, talkRoomId);
      if (keys.length >= TALK_BOOKMARK_PER_ROOM_MAX) {
        return false;
      }
    }

    if (value) {
      const res = await PutRequest<{ statusCode: number }>(`/talk/bookmark/${talkId}`, "");
      if (!res || res.statusCode !== 200) {
        throw new Error(`Failed put bookmark: response=${JSON.stringify(res)}`);
      }
    } else {
      const res = await DeleteRequest<{ statusCode: number }>(`/talk/bookmark/${talkId}`);
      if (!res || res.statusCode !== 200) {
        throw new Error(`Failed delete bookmark: response=${JSON.stringify(res)}`);
      }
    }
    return true;
  }

  async getPlanOwners(planId: string): Promise<TalkRoomUser[]> {
    const res = await GetRequest<{ statusCode: number; owners: TalkRoomUser[] }>(`/plan/${planId}/owners`);
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.getPlanOwners API Error: ${JSON.stringify(res)}`);
    }
    return res.owners.map(o => {
      o.isOwner = true;
      return o;
    });
  }

  async getPlanMembers(planId: string, userName: string, page = 1, pageSize = 20): Promise<TalkRoomUser[]> {
    const paramsObj: { [name: string]: string } = {
      user_name: userName,
      page: page.toString(),
      page_size: pageSize.toString(),
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<{ statusCode: number; members: TalkRoomUser[] }>(
      `/plan/${planId}/members?` + searchParams.toString(),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.getPlanMembers API Error: ${JSON.stringify(res)}`);
    }
    return res.members;
  }

  async getTalkRoomDetail(planId: string, talkRoomId: string): Promise<TalkRoom> {
    const res = await GetRequest<{ statusCode: number; talkRoom: TalkRoom }>(
      `/talk/room/${planId}/detail/${talkRoomId}`,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`TalkRoomRepository.getTalkRoomDetail API Error: ${JSON.stringify(res)}`);
    }
    // 詳細APIはrootにtalkroomがあるため、talkroomとして返す
    return res.talkRoom;
  }
}
